/* ==========================================================================
   ORVEA — theme behaviour
   Vanilla JS, no dependencies. Each behaviour is opt-in via a data attribute
   so sections only pay for what they use.
   ========================================================================== */
(function () {
  'use strict';

  /**
   * Every ORVEA section emits this script tag so sections stay self-contained;
   * the browser fetches the file once but executes each tag, so boot exactly
   * once and let the first instance own the listeners.
   */
  if (window.__ORVEA_BOOTED__) return;
  window.__ORVEA_BOOTED__ = true;

  var ROOT = document;

  function $(sel, ctx) {
    return (ctx || ROOT).querySelector(sel);
  }
  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || ROOT).querySelectorAll(sel));
  }
  function on(el, ev, fn, opts) {
    if (el) el.addEventListener(ev, fn, opts);
  }

  /* ----------------------------------------------------------------------
     Money formatting — mirrors shop.money_format placeholders.
     -------------------------------------------------------------------- */
  function formatMoney(cents, format) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    var fmt = format || (window.ORVEA && window.ORVEA.moneyFormat) || '${{amount}}';

    function withDelimiters(number, precision, thousands, decimal) {
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var frac = parts[1] ? decimal + parts[1] : '';
      return whole + frac;
    }

    var value = '';
    var match = /\{\{\s*(\w+)\s*\}\}/.exec(fmt);
    var placeholder = match ? match[1] : 'amount';

    switch (placeholder) {
      case 'amount':
        value = withDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = withDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }

    return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /* ----------------------------------------------------------------------
     Cart count — kept in sync after any add.
     -------------------------------------------------------------------- */
  function refreshCartCount() {
    return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        $$('[data-orvea-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
        /* The drawer's "Ver cesta (n)" counter is always shown, even at zero. */
        $$('[data-orvea-cart-count-plain]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
        ROOT.dispatchEvent(new CustomEvent('orvea:cart:updated', { detail: { cart: cart }, bubbles: true }));
        return cart;
      })
      .catch(function () {
        /* non-fatal */
      });
  }

  function addToCart(payload) {
    var url = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js';
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw new Error(data.description || data.message || 'Cart error');
        return data;
      });
    });
  }

  /* ----------------------------------------------------------------------
     Quick add — the "+" button on every product card.
     -------------------------------------------------------------------- */
  function initQuickAdd(scope) {
    $$('[data-orvea-quick-add]', scope).forEach(function (btn) {
      if (btn.dataset.orveaBound === 'true') return;
      btn.dataset.orveaBound = 'true';

      on(btn, 'click', function (event) {
        event.preventDefault();
        var id = btn.getAttribute('data-variant-id');
        if (!id) return;

        var original = btn.innerHTML;
        btn.setAttribute('aria-busy', 'true');

        /* orveaAdd also opens and refreshes the cart drawer — see the cart
           drawer module further down. Declared later in this file, which is
           fine: function declarations hoist and this runs on click. */
        orveaAdd([{ id: Number(id), quantity: 1 }])
          .then(function () {
            btn.innerHTML = '&#10003;';
            return refreshCartCount();
          })
          .then(function () {
            window.setTimeout(function () {
              btn.innerHTML = original;
              btn.removeAttribute('aria-busy');
            }, 1400);
          })
          .catch(function (err) {
            btn.removeAttribute('aria-busy');
            btn.innerHTML = original;
            var live = $('[data-orvea-live-region]');
            if (live) live.textContent = err.message;
          });
      });
    });
  }

  /* ----------------------------------------------------------------------
     Accordions — footer groups and product detail panels.
     -------------------------------------------------------------------- */
  function initAccordions(scope) {
    $$('[data-orvea-accordion]', scope).forEach(function (acc) {
      if (acc.dataset.orveaBound === 'true') return;
      acc.dataset.orveaBound = 'true';
      var single = acc.getAttribute('data-orvea-accordion') === 'single';

      /**
       * Some accordions (the footer link groups) are only collapsible on small
       * screens; above the breakpoint every panel stays open and the trigger is
       * disabled rather than left as a button that does nothing.
       */
      var desktopOpen = acc.getAttribute('data-orvea-accordion-desktop') === 'open';
      if (desktopOpen && window.matchMedia) {
        var mq = window.matchMedia('(min-width: 990px)');
        var applyDesktop = function () {
          $$('[data-orvea-acc-trigger]', acc).forEach(function (trigger) {
            var panel = ROOT.getElementById(trigger.getAttribute('aria-controls'));
            if (mq.matches) {
              trigger.setAttribute('aria-expanded', 'true');
              trigger.setAttribute('aria-disabled', 'true');
              trigger.disabled = true;
              if (panel) panel.setAttribute('data-open', 'true');
            } else {
              trigger.removeAttribute('aria-disabled');
              trigger.disabled = false;
            }
          });
        };
        applyDesktop();
        if (mq.addEventListener) mq.addEventListener('change', applyDesktop);
        else if (mq.addListener) mq.addListener(applyDesktop);
      }

      $$('[data-orvea-acc-trigger]', acc).forEach(function (trigger) {
        on(trigger, 'click', function () {
          if (trigger.disabled) return;
          var panel = ROOT.getElementById(trigger.getAttribute('aria-controls'));
          var isOpen = trigger.getAttribute('aria-expanded') === 'true';

          if (single && !isOpen) {
            $$('[data-orvea-acc-trigger]', acc).forEach(function (other) {
              if (other === trigger) return;
              other.setAttribute('aria-expanded', 'false');
              var otherPanel = ROOT.getElementById(other.getAttribute('aria-controls'));
              if (otherPanel) otherPanel.setAttribute('data-open', 'false');
            });
          }

          trigger.setAttribute('aria-expanded', String(!isOpen));
          if (panel) panel.setAttribute('data-open', String(!isOpen));
        });
      });
    });
  }

  /* ----------------------------------------------------------------------
     Rail scroll progress indicator.
     -------------------------------------------------------------------- */
  function initRails(scope) {
    $$('[data-orvea-rail]', scope).forEach(function (rail) {
      if (rail.dataset.orveaBound === 'true') return;
      rail.dataset.orveaBound = 'true';

      var wrapper = rail.closest('[data-orvea-rail-wrapper]');
      var bar = wrapper ? $('[data-orvea-rail-bar]', wrapper) : null;
      if (!bar) return;

      var update = function () {
        var scrollable = rail.scrollWidth - rail.clientWidth;
        if (scrollable <= 1) {
          bar.style.transform = 'scaleX(1)';
          bar.style.marginInlineStart = '0';
          return;
        }
        var ratio = rail.clientWidth / rail.scrollWidth;
        var progress = rail.scrollLeft / scrollable;
        bar.style.width = Math.max(ratio * 100, 12) + '%';
        bar.style.transform = 'translateX(' + progress * ((100 / Math.max(ratio * 100, 12)) * 100 - 100) + '%)';
      };

      on(rail, 'scroll', update, { passive: true });
      if (window.ResizeObserver) new ResizeObserver(update).observe(rail);
      update();
    });
  }

  /* ----------------------------------------------------------------------
     Mobile navigation drawer — full-screen overlay from the source design.
     -------------------------------------------------------------------- */
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function initMenu(scope) {
    $$('[data-orvea-menu]', scope).forEach(function (menu) {
      if (menu.dataset.orveaBound === 'true') return;
      menu.dataset.orveaBound = 'true';

      var id = menu.id;
      var openers = $$('[data-orvea-menu-open][aria-controls="' + id + '"]');
      var closers = $$('[data-orvea-menu-close]', menu);
      var lastFocused = null;

      function open() {
        lastFocused = ROOT.activeElement;
        menu.hidden = false;
        // Force a frame so the transition runs.
        window.requestAnimationFrame(function () {
          menu.setAttribute('data-open', 'true');
        });
        ROOT.documentElement.style.overflow = 'hidden';
        openers.forEach(function (o) {
          o.setAttribute('aria-expanded', 'true');
        });
        var first = $(FOCUSABLE, menu);
        if (first) first.focus();
      }

      function close() {
        menu.setAttribute('data-open', 'false');
        ROOT.documentElement.style.overflow = '';
        openers.forEach(function (o) {
          o.setAttribute('aria-expanded', 'false');
        });
        window.setTimeout(function () {
          if (menu.getAttribute('data-open') !== 'true') menu.hidden = true;
        }, 320);
        if (lastFocused && lastFocused.focus) lastFocused.focus();
      }

      openers.forEach(function (o) {
        on(o, 'click', open);
      });
      closers.forEach(function (c) {
        on(c, 'click', close);
      });

      on(menu, 'keydown', function (event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
          return;
        }
        if (event.key !== 'Tab') return;
        var items = $$(FOCUSABLE, menu).filter(function (el) {
          return el.offsetParent !== null;
        });
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (event.shiftKey && ROOT.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && ROOT.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    });
  }

  /* ----------------------------------------------------------------------
     Quantity stepper.
     -------------------------------------------------------------------- */
  function initQuantity(scope) {
    $$('[data-orvea-quantity]', scope).forEach(function (wrap) {
      if (wrap.dataset.orveaBound === 'true') return;
      wrap.dataset.orveaBound = 'true';

      var input = $('input[type="number"]', wrap);
      if (!input) return;

      $$('[data-orvea-qty-step]', wrap).forEach(function (btn) {
        on(btn, 'click', function () {
          var step = parseInt(btn.getAttribute('data-orvea-qty-step'), 10) || 1;
          var min = parseInt(input.getAttribute('min'), 10) || 1;
          var max = input.getAttribute('max') ? parseInt(input.getAttribute('max'), 10) : Infinity;
          var next = (parseInt(input.value, 10) || min) + step;
          input.value = String(Math.min(Math.max(next, min), max));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ----------------------------------------------------------------------
     Product variant picker — price, availability, gallery, URL.
     -------------------------------------------------------------------- */
  function initVariantPickers(scope) {
    $$('[data-orvea-product]', scope).forEach(function (root) {
      if (root.dataset.orveaBound === 'true') return;
      root.dataset.orveaBound = 'true';

      var dataEl = $('[data-orvea-variants]', root);
      if (!dataEl) return;

      var variants;
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }
      if (!Array.isArray(variants) || !variants.length) return;

      var inputs = $$('[data-orvea-option-input]', root);
      var idInput = $('[data-orvea-variant-id]', root);
      var priceEl = $('[data-orvea-price]', root);
      var compareEl = $('[data-orvea-compare]', root);
      var savingEl = $('[data-orvea-saving]', root);
      var submitEl = $('[data-orvea-submit]', root);
      var availabilityEl = $('[data-orvea-availability]', root);
      var unitEl = $('[data-orvea-unit-price]', root);
      var handle = root.getAttribute('data-product-url');
      var soldOutText = root.getAttribute('data-sold-out-text') || 'Sold out';
      var addText = root.getAttribute('data-add-text') || 'Add to cart';
      var unavailableText = root.getAttribute('data-unavailable-text') || 'Unavailable';

      function selectedOptions() {
        var values = [];
        inputs.forEach(function (input) {
          var index = parseInt(input.getAttribute('data-option-index'), 10);
          if (input.tagName === 'SELECT') {
            values[index] = input.value;
          } else if (input.checked) {
            values[index] = input.value;
          }
        });
        return values;
      }

      function matchVariant(values) {
        return (
          variants.filter(function (variant) {
            return values.every(function (value, index) {
              if (value === undefined) return true;
              return variant.options[index] === value;
            });
          })[0] || null
        );
      }

      function updateGallery(variant) {
        // Liquid's variant JSON exposes the media as an object, not a flat id.
        var mediaId = variant && variant.featured_media ? variant.featured_media.id : null;
        if (mediaId == null) return;
        var target = $('[data-orvea-gallery]', root);
        if (!target) return;
        var slide = $('[data-media-id="' + mediaId + '"]', target);
        if (!slide) return;
        if (typeof slide.scrollIntoView === 'function') {
          slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }
        $$('[data-orvea-gallery-thumb]', root).forEach(function (thumb) {
          var active = thumb.getAttribute('data-media-id') === String(mediaId);
          thumb.setAttribute('aria-current', active ? 'true' : 'false');
        });
      }

      function render(variant) {
        if (idInput) idInput.value = variant ? variant.id : '';

        if (!variant) {
          if (submitEl) {
            submitEl.disabled = true;
            var t0 = $('[data-orvea-submit-text]', submitEl) || submitEl;
            t0.textContent = unavailableText;
          }
          if (priceEl) priceEl.textContent = '';
          if (compareEl) compareEl.hidden = true;
          if (savingEl) savingEl.hidden = true;
          if (availabilityEl) availabilityEl.hidden = true;
          return;
        }

        if (priceEl) priceEl.textContent = formatMoney(variant.price);

        if (compareEl) {
          var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
          compareEl.hidden = !onSale;
          if (onSale) compareEl.textContent = formatMoney(variant.compare_at_price);
          if (savingEl) {
            savingEl.hidden = !onSale;
            if (onSale) {
              var pct = Math.round(
                ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
              );
              savingEl.textContent = '-' + pct + '%';
            }
          }
        }

        if (unitEl) {
          if (variant.unit_price) {
            unitEl.hidden = false;
            unitEl.textContent =
              formatMoney(variant.unit_price) +
              ' / ' +
              (variant.unit_price_measurement
                ? (variant.unit_price_measurement.reference_value || 1) +
                  ' ' +
                  variant.unit_price_measurement.reference_unit
                : '');
          } else {
            unitEl.hidden = true;
          }
        }

        if (submitEl) {
          submitEl.disabled = !variant.available;
          var textEl = $('[data-orvea-submit-text]', submitEl) || submitEl;
          textEl.textContent = variant.available ? addText : soldOutText;
        }

        if (availabilityEl) {
          availabilityEl.hidden = false;
          availabilityEl.textContent = availabilityEl.getAttribute(
            variant.available ? 'data-in-stock' : 'data-out-of-stock'
          );
        }

        updateGallery(variant);

        if (handle && window.history && window.history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }

        ROOT.dispatchEvent(
          new CustomEvent('orvea:variant:change', { detail: { variant: variant }, bubbles: true })
        );
      }

      inputs.forEach(function (input) {
        on(input, 'change', function () {
          render(matchVariant(selectedOptions()));
        });
      });

      // Gallery thumbnails
      $$('[data-orvea-gallery-thumb]', root).forEach(function (thumb) {
        on(thumb, 'click', function () {
          var gallery = $('[data-orvea-gallery]', root);
          if (!gallery) return;
          var slide = $('[data-media-id="' + thumb.getAttribute('data-media-id') + '"]', gallery);
          if (slide) slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          $$('[data-orvea-gallery-thumb]', root).forEach(function (t) {
            t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
          });
        });
      });

      // Async add-to-cart so the page never navigates away.
      var form = $('[data-orvea-product-form]', root);
      if (form && form.getAttribute('data-orvea-async') === 'true') {
        on(form, 'submit', function (event) {
          event.preventDefault();
          if (!submitEl || submitEl.disabled) return;
          var formData = new FormData(form);
          var items = {
            id: Number(formData.get('id')),
            quantity: Number(formData.get('quantity') || 1)
          };
          var textEl = $('[data-orvea-submit-text]', submitEl) || submitEl;
          var previous = textEl.textContent;
          submitEl.setAttribute('aria-busy', 'true');

          addToCart({ items: [items] })
            .then(function () {
              textEl.textContent = submitEl.getAttribute('data-added-text') || 'Added';
              return refreshCartCount();
            })
            .then(function () {
              window.setTimeout(function () {
                textEl.textContent = previous;
                submitEl.removeAttribute('aria-busy');
              }, 1600);
            })
            .catch(function (err) {
              submitEl.removeAttribute('aria-busy');
              textEl.textContent = previous;
              var live = $('[data-orvea-live-region]', root);
              if (live) live.textContent = err.message;
            });
        });
      }
    });
  }

  /* ----------------------------------------------------------------------
     Auto-submitting selects (localisation form).
     -------------------------------------------------------------------- */
  function initAutoSubmit(scope) {
    $$('[data-orvea-auto-submit]', scope).forEach(function (select) {
      if (select.dataset.orveaBound === 'true') return;
      select.dataset.orveaBound = 'true';
      on(select, 'change', function () {
        if (select.form) select.form.submit();
      });
    });
  }

  /* ----------------------------------------------------------------------
     Announcement rotator.
     -------------------------------------------------------------------- */
  function initRotators(scope) {
    $$('[data-orvea-rotator]', scope).forEach(function (track) {
      if (track.dataset.orveaBound === 'true') return;
      track.dataset.orveaBound = 'true';

      var items = $$('[data-orvea-rotator-item]', track);
      if (items.length < 2) return;

      var interval = parseInt(track.getAttribute('data-interval'), 10) || 5000;
      var index = 0;
      var timer = null;

      function show(next) {
        items[index].hidden = true;
        index = (next + items.length) % items.length;
        items[index].hidden = false;
      }

      function start() {
        stop();
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        timer = window.setInterval(function () {
          show(index + 1);
        }, interval);
      }

      function stop() {
        if (timer) window.clearInterval(timer);
        timer = null;
      }

      on(track, 'mouseenter', stop);
      on(track, 'mouseleave', start);
      on(track, 'focusin', stop);
      on(track, 'focusout', start);
      start();
    });
  }
  /* ----------------------------------------------------------------------
     Thresholds — live free-shipping / gift progress.

     The server renders the correct state on first paint; from then on this
     module keeps it in sync with the real cart, with no page reload.

     Three things feed it, in order of preference:

       1. Shopify's Storefront Standard Events, which Horizon dispatches for
          every cart mutation it owns ('shopify:cart:lines-update' and
          friends). The event fires *before* the network call finishes and
          carries `event.promise`, which resolves with the new cart — so we
          paint optimistically from that the moment it lands.
       2. An authoritative re-read of /cart.js, always issued alongside (1).
          It is the only source that reports the total in integer cents, so
          it is what we trust; the optimistic paint just hides its latency.
       3. A fetch/XHR interceptor, as a safety net for anything that mutates
          the cart without announcing it (apps, third-party add-to-cart).

     Every read carries a monotonic sequence number, so a slow response can
     never overwrite the result of a newer one.
     -------------------------------------------------------------------- */

  /* Matches /cart/add|change|update|clear, with or without .js, on any
     locale-prefixed root (/fr-ca/cart/add.js). Deliberately does NOT match
     plain /cart.js — that is our own read, not a mutation. */
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|#|$)/i;

  var cartSeq = 0; /* incremented once per sync attempt */
  var appliedSeq = 0; /* highest sequence already painted */
  var cartAbort = null; /* in-flight /cart.js read, so it can be cancelled */
  var mutationTimer = null;
  var lastKnownTotal = null; /* cents; null until the first read lands */
  var lastSyncAt = 0; /* ms timestamp of the last sync we started */
  var SYNC_COALESCE_MS = 300;

  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /**
   * Standard Events report money as a currency-decimal string ("58.00").
   * Parse it by string rather than float maths so 0.1 + 0.2 can never bite,
   * and treat anything unexpected as unknown rather than guessing.
   */
  function amountToCents(amount) {
    if (typeof amount === 'number') return Math.round(amount * 100);
    if (typeof amount !== 'string') return null;
    var s = amount.trim();
    if (!/^\d+(\.\d+)?$/.test(s)) return null;
    var parts = s.split('.');
    var frac = (parts[1] || '').slice(0, 2);
    while (frac.length < 2) frac += '0';
    return parseInt(parts[0], 10) * 100 + parseInt(frac, 10);
  }

  /**
   * Thresholds use the Spanish convention ("7,20 €"), while the rest of the
   * theme keeps the store's own format. The pattern is built server-side by
   * snippets/orvea-money.liquid from the same Shopify setting, so the number
   * here is byte-identical to the one Liquid rendered — only the symbol moves.
   */
  function formatMoneyShort(cents) {
    var pattern = (window.ORVEA && window.ORVEA.thresholdMoneyFormat) || null;
    var out = formatMoney(cents, pattern);
    if (cents % 100 === 0) out = out.replace(/[.,]00(?!\d)/, '');
    return out;
  }

  function fillTemplate(tpl, cents) {
    return String(tpl || '').replace('[importe]', formatMoneyShort(cents));
  }

  /** Flash the "just unlocked" treatment, once, and clean up after itself. */
  function celebrate(el) {
    if (!el) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    el.classList.remove('is-unlocking');
    /* Force a reflow so re-adding the class restarts the animation. */
    void el.offsetWidth;
    el.classList.add('is-unlocking');
    window.setTimeout(function () {
      el.classList.remove('is-unlocking');
    }, 1400);
  }

  /**
   * @param {Element} root
   * @param {number} total   new cart total, in cents
   * @param {number|null} prev  the previous known total, or null on first read.
   *        Crossings are detected from these two numbers rather than from the
   *        DOM, so the animation still fires when the host theme has just
   *        replaced our markup with freshly server-rendered HTML.
   */
  function paintThresholds(root, total, prev) {
    var t1 = parseInt(root.getAttribute('data-threshold-1'), 10) || 0;
    var t2 = parseInt(root.getAttribute('data-threshold-2'), 10) || 0;
    var scale = parseInt(root.getAttribute('data-scale'), 10) || 0;
    var showSecond = root.getAttribute('data-show-second') === 'true';

    var crossings = [];

    var states = [
      {
        n: 1,
        target: t1,
        tplTo: root.getAttribute('data-tpl-to-first'),
        tplDone: root.getAttribute('data-tpl-first-done')
      }
    ];
    if (showSecond) {
      states.push({
        n: 2,
        target: t2,
        tplTo: root.getAttribute('data-tpl-to-second'),
        tplDone: root.getAttribute('data-tpl-second-done')
      });
    }

    states.forEach(function (state) {
      var line = $('[data-orvea-status-line="' + state.n + '"]', root);
      var item = $('[data-orvea-threshold-item="' + state.n + '"]', root);

      var done = total >= state.target;
      var remaining = Math.max(state.target - total, 0);
      var justUnlocked = prev !== null && prev < state.target && done;

      if (line) {
        line.setAttribute('data-done', done ? 'true' : 'false');

        var text = $('[data-orvea-status-text="' + state.n + '"]', line);
        if (text) text.textContent = done ? state.tplDone || '' : fillTemplate(state.tplTo, remaining);

        var pending = $('[data-orvea-status-pending]', line);
        var doneIcon = $('[data-orvea-status-done]', line);
        if (pending) pending.hidden = done;
        if (doneIcon) doneIcon.hidden = !done;
      }

      if (item) item.setAttribute('data-unlocked', done ? 'true' : 'false');
      root.setAttribute('data-unlocked-' + state.n, done ? 'true' : 'false');

      /* Milestone circles on the bar (cart page): the pending icon — truck or
         gift — crossfades to the check the moment its threshold is cleared.
         Only the flag changes here; the crossfade itself is CSS. */
      var milestone = $('[data-orvea-threshold-milestone="' + state.n + '"]', root);
      if (milestone) milestone.setAttribute('data-done', done ? 'true' : 'false');

      /* The reward slot, if the merchant configured one, appears the moment
         the gift threshold is cleared — no reload, no leaving the page.
         Anything dropped inside it inherits this behaviour for free. */
      if (state.n === 2) {
        var reward = $('[data-orvea-threshold-reward]', root);
        if (reward) reward.hidden = !done;
      }

      if (justUnlocked) {
        celebrate(line);
        celebrate(item);
        celebrate($('[data-orvea-threshold-progress]', root));
      }

      crossings.push({ index: state.n, target: state.target, unlocked: done, justUnlocked: justUnlocked });
    });

    var bar = $('[data-orvea-threshold-bar]', root);
    if (bar && scale > 0) {
      bar.style.width = Math.min(Math.round((total / scale) * 100), 100) + '%';
    }

    root.setAttribute('data-cart-total', total);

    return crossings;
  }

  /**
   * Paint every instance on the page, unless a newer read has already
   * landed — that is what makes rapid successive cart changes safe.
   */
  function applyTotal(totalCents, seq) {
    if (typeof totalCents !== 'number' || isNaN(totalCents) || totalCents < 0) return;
    if (seq < appliedSeq) return;
    appliedSeq = seq;
    var prev = lastKnownTotal;
    lastKnownTotal = totalCents;

    var crossings = [];
    $$('[data-orvea-thresholds]').forEach(function (root) {
      var painted = paintThresholds(root, totalCents, prev);
      if (painted && painted.length > crossings.length) crossings = painted;
    });

    /* Public hook. Dispatched once per actual cart change, no matter how many
       instances are on the page (product block + cart drawer, typically) and
       no matter how many reads confirmed it — so a listener never has to
       de-duplicate. Other scripts can listen for it instead of re-deriving
       cart state:

         document.addEventListener('orvea:thresholds:change', function (e) {
           var gift = e.detail.thresholds[1];
           if (gift && gift.justUnlocked) { ... }
         });
    */
    var unlockedNow = crossings.some(function (c) {
      return c.justUnlocked;
    });
    if (prev !== totalCents || unlockedNow) {
      ROOT.dispatchEvent(
        new CustomEvent('orvea:thresholds:change', {
          detail: { total: totalCents, previousTotal: prev, thresholds: crossings }
        })
      );
    }
  }

  /**
   * The ORVEA Club gift card in the cart's main column. The card is rendered
   * by Liquid with the state the server knows; this keeps it honest between
   * renders, so a quantity change flips it to "unlocked" without a reload.
   * Membership is never decided here — the card carries what Liquid resolved
   * from `customer`, and this only compares the eligible subtotal with the
   * card's own threshold.
   */
  function paintGiftCard(cart) {
    var card = $('[data-orvea-gift-card]');
    if (!card || !cart || !cart.items) return;
    if (card.getAttribute('data-member') !== 'true') return;

    var threshold = parseInt(card.getAttribute('data-threshold'), 10) || 0;
    var eligible = readRewardState(cart).eligible;
    card.setAttribute('data-unlocked', eligible >= threshold ? 'true' : 'false');
  }

  /** The authoritative read: /cart.js is the only source that gives cents. */
  function readCart(seq) {
    if (cartAbort) {
      try {
        cartAbort.abort();
      } catch (e) {
        /* older browsers */
      }
    }
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    cartAbort = controller;

    return fetch(shopRoot() + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal: controller ? controller.signal : undefined
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (cart && typeof cart.total_price === 'number') applyTotal(cart.total_price, seq);
        if (cart && cart.items) {
          paintReward(cart);
          paintGiftCard(cart);
          enforceGiftEligibility(cart);
        }
        return cart;
      })
      .catch(function () {
        /* aborted or offline — the last painted state stays put */
      })
      .then(function (cart) {
        if (cartAbort === controller) cartAbort = null;
        return cart;
      });
  }

  /**
   * @param {number|null} [optimisticCents] a total we already believe, painted
   *        straight away so the bar moves in the same frame as the cart does.
   */
  function syncThresholds(optimisticCents) {
    /* Two consumers: the threshold ladder (product page, /cart) and the cart
       drawer's reward block. The drawer can be the only one on the page — the
       home page has no ladder — so bailing out on the ladder alone would leave
       `enforceGiftEligibility` unrun and an invalid gift line in the cart. */
    if (!$('[data-orvea-thresholds]') && !$('[data-orvea-reward]') && !$('[data-orvea-gift-card]')) return;
    lastSyncAt = Date.now();
    var seq = ++cartSeq;
    if (typeof optimisticCents === 'number') applyTotal(optimisticCents, seq);
    return readCart(seq);
  }

  /**
   * The network safety net. Horizon's own mutations already trigger a sync via
   * their Standard Event, so skip if one just started: a /cart.js read returns
   * the whole cart, and therefore already covers anything that landed in the
   * same moment. Nothing is missed, and a normal add stops costing three reads.
   */
  function scheduleSync() {
    if (Date.now() - lastSyncAt < SYNC_COALESCE_MS) return;
    if (mutationTimer) window.clearTimeout(mutationTimer);
    mutationTimer = window.setTimeout(function () {
      mutationTimer = null;
      if (Date.now() - lastSyncAt < SYNC_COALESCE_MS) return;
      syncThresholds();
    }, 50);
  }

  /* --- 1. Shopify Storefront Standard Events (what Horizon dispatches) --- */

  function onStandardCartEvent(event) {
    var promise = event && event.promise;
    if (!promise || typeof promise.then !== 'function') {
      scheduleSync();
      return;
    }
    promise.then(
      function (result) {
        var cents = null;
        var cart = result && result.cart;
        if (cart && cart.cost && cart.cost.totalAmount) {
          cents = amountToCents(cart.cost.totalAmount.amount);
        }
        syncThresholds(typeof cents === 'number' ? cents : undefined);
      },
      function () {
        /* The mutation failed; re-read so we reflect whatever did stick. */
        syncThresholds();
      }
    );
  }

  [
    'shopify:cart:lines-update',
    'shopify:cart:discount-update',
    'shopify:cart:note-update',
    'shopify:cart:error'
  ].forEach(function (name) {
    on(ROOT, name, onStandardCartEvent);
  });

  /* Our own add-to-cart path hands us the cart directly. */
  on(ROOT, 'orvea:cart:updated', function (event) {
    var cart = event.detail && event.detail.cart;
    syncThresholds(cart && typeof cart.total_price === 'number' ? cart.total_price : undefined);
  });

  /* Older/other themes and apps use these names; harmless if never fired. */
  ['cart:update', 'cart:updated', 'cart:refresh', 'cart-update'].forEach(function (name) {
    on(ROOT, name, scheduleSync);
  });

  /* --- 3. Network safety net ------------------------------------------- */

  function urlOf(input) {
    try {
      if (typeof input === 'string') return input;
      if (input && typeof input.url === 'string') return input.url;
      if (input && typeof input.toString === 'function') return input.toString();
    } catch (e) {
      /* exotic Request objects */
    }
    return '';
  }

  (function patchFetch() {
    if (typeof window.fetch !== 'function' || window.__ORVEA_FETCH_PATCHED__) return;
    window.__ORVEA_FETCH_PATCHED__ = true;
    var original = window.fetch;

    window.fetch = function (input) {
      var isMutation = false;
      try {
        isMutation = CART_MUTATION_RE.test(urlOf(input));
      } catch (e) {
        /* never let instrumentation break a request */
      }
      var result = original.apply(this, arguments);
      if (isMutation && result && typeof result.then === 'function') {
        result.then(scheduleSync, function () {});
      }
      return result;
    };
  })();

  (function patchXhr() {
    if (typeof window.XMLHttpRequest !== 'function' || window.__ORVEA_XHR_PATCHED__) return;
    window.__ORVEA_XHR_PATCHED__ = true;
    var proto = window.XMLHttpRequest.prototype;
    var originalOpen = proto.open;
    var originalSend = proto.send;

    proto.open = function (method, url) {
      try {
        this.__orveaCartMutation = CART_MUTATION_RE.test(String(url || ''));
      } catch (e) {
        this.__orveaCartMutation = false;
      }
      return originalOpen.apply(this, arguments);
    };

    proto.send = function () {
      if (this.__orveaCartMutation) {
        try {
          this.addEventListener('loadend', scheduleSync);
        } catch (e) {
          /* ignore */
        }
      }
      return originalSend.apply(this, arguments);
    };
  })();

  /* --- Boot ------------------------------------------------------------- */

  function initThresholds(scope) {
    if (!$$('[data-orvea-thresholds]', scope).length && !$$('[data-orvea-reward]', scope).length) return;

    /* Re-verify once per page: the document may have been served from cache
       with a stale cart total. This is also the pass that removes a gift line
       left over from a cart that has since dropped below the threshold. */
    if (!ROOT.documentElement.dataset.orveaThresholdsHydrated) {
      ROOT.documentElement.dataset.orveaThresholdsHydrated = 'true';
      syncThresholds();
    }
  }

  /* Back/forward cache restores a stale document. */
  on(window, 'pageshow', function (event) {
    if (event.persisted) syncThresholds();
  });

  /* ----------------------------------------------------------------------
     Cart reward — the €50 / €70 ladder inside the cart drawer.

     Eligibility is computed from the REAL cart, always excluding lines marked
     with the `_orvea_regalo` property, exactly like the automatic discount
     (which measures the threshold over the "Elegible para regalo" collection).
     A gift therefore can never help unlock itself.

     This module owns cart *state*, not just pixels: when the cart drops below
     the gift threshold it removes the gift line instead of merely hiding a
     button, so an invalid reward can never reach checkout. The €0 price comes
     from the Shopify automatic discount, never from this file.
     -------------------------------------------------------------------- */

  var GIFT_PROPERTY = '_orvea_regalo';
  var rewardBusy = false;

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /**
   * @returns {{eligible:number, giftLine:object|null, giftLines:object[]}}
   *   `eligible` never counts a gift line, so a gift cannot help unlock itself.
   *   `giftLines` is every gift line found — there should only ever be one, but
   *   the whole point of the guard below is that we do not assume that.
   */
  function readRewardState(cart) {
    var eligible = 0;
    var giftLines = [];
    (cart.items || []).forEach(function (line) {
      var props = line.properties || {};
      if (props[GIFT_PROPERTY]) {
        giftLines.push(line);
      } else {
        eligible += line.final_line_price;
      }
    });
    return { eligible: eligible, giftLine: giftLines[0] || null, giftLines: giftLines };
  }

  function changeLine(id, quantity, properties) {
    var body = { id: id, quantity: quantity };
    if (properties) body.properties = properties;
    return fetch(cartRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json();
    });
  }

  /** Drop every gift line. Plural on purpose: a stale cart may hold more. */
  function removeAllGiftLines(cart) {
    var keys = (cart.items || [])
      .filter(function (line) {
        return (line.properties || {})[GIFT_PROPERTY];
      })
      .map(function (line) {
        return line.key;
      });

    if (!keys.length) return Promise.resolve(cart);

    return keys.reduce(function (chain, key) {
      return chain.then(function () {
        return changeLine(key, 0);
      });
    }, Promise.resolve());
  }

  function fetchCart() {
    return fetch(cartRoot() + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) {
      return r.json();
    });
  }

  /**
   * The guard that makes the UI honest: if the eligible subtotal is under the
   * gift threshold, any gift line is removed from the actual cart.
   */
  /** Add one gift line, marked so the rest of the system recognises it. */
  function addGiftLine(variantId) {
    var props = {};
    props[GIFT_PROPERTY] = 'si';
    return fetch(cartRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties: props }] })
    }).then(function (r) {
      if (!r.ok) throw new Error('gift add failed');
      return r.json();
    });
  }

  /* One failed attempt at the monthly gift (sold out between the render and
     the add, say) is enough: without this the next cart read would try again,
     and again, on every change. Cleared when the page reloads. */
  var monthlyAddFailed = false;

  /**
   * The gift of the month is for logged-in members only, and it is never
   * chosen: one unit of the configured product, added automatically.
   *
   *   · member, eligible ≥ threshold → exactly one line of the monthly gift
   *   · anyone else (guest, or below the threshold, or no gift available)
   *                                   → no gift line at all
   *
   * `data-member` comes from Liquid's `customer` (the real Shopify session);
   * `data-monthly-variant` is only non-zero for a member, and only when the
   * Liquid confirmed the product is in stock and inside the discounted
   * collection. The live cart is always what gets corrected, not just the UI.
   */
  function enforceGiftEligibility(cart) {
    /* Two surfaces publish the same three facts — membership, the gift's
       variant and the threshold — so the one rule can run wherever the
       customer happens to be: the cart drawer's reward block, and (on /cart,
       where no drawer is rendered) the ORVEA Club gift card. Same logic, same
       guards; only the element it reads them from differs. */
    var root = $('[data-orvea-reward]') || $('[data-orvea-gift-card]');
    if (!root) return Promise.resolve(cart);
    if (rewardBusy) return Promise.resolve(cart);

    var threshold =
      parseInt(root.getAttribute('data-threshold-gift') || root.getAttribute('data-threshold'), 10) || 0;
    var state = readRewardState(cart);
    var isMember = root.getAttribute('data-member') === 'true';
    var monthly = String(root.getAttribute('data-monthly-variant') || '0');
    var wantsGift = isMember && monthly !== '0' && state.eligible >= threshold;

    function finish(promise, isAdd) {
      rewardBusy = true;
      var settled = promise.then(fetchCart);

      /* On /cart there is no drawer to re-render, and the page's own line
         list only reacts to Shopify's standard cart event. Handing Horizon
         the very same promise lets it refresh the list itself, so the gift
         appears — and disappears — without a reload.

         A removal has to be announced just like an add: otherwise the gift
         line stays on screen and the summary goes on charging for a gift
         that is no longer in the cart. And the announcement waits for the
         page to be listening (`cartPageReady`), because on a first load the
         gift can settle before Horizon has upgraded its components.

         The drawer path is left exactly as it was: it has
         `refreshCartDrawer` for this, and announcing an add there would pop
         the drawer open on its own. */
      if (!cartDrawerInner()) {
        cartPageReady().then(function () {
          announceAdd(settled, isAdd ? 'add' : 'update');
        });
      }

      return settled
        .then(function (fresh) {
          rewardBusy = false;
          announceCartChange(fresh);
          return fresh;
        })
        .catch(function () {
          rewardBusy = false;
          return cart;
        });
    }

    /* No gift for this cart: every gift line comes out of the real cart. */
    if (!wantsGift) {
      if (!state.giftLines.length) return Promise.resolve(cart);
      return finish(removeAllGiftLines(cart));
    }

    /* Member above the threshold with no gift yet: add the gift of the month. */
    if (!state.giftLines.length) {
      if (monthlyAddFailed) return Promise.resolve(cart);
      return finish(
        addGiftLine(monthly).catch(function (err) {
          monthlyAddFailed = true;
          throw err;
        }),
        true
      );
    }

    /* Exactly one line of the monthly gift, quantity one. Anything else —
       a duplicate, extra quantity, or a different product left over from an
       earlier configuration — is corrected here. */
    var keep = null;
    state.giftLines.forEach(function (line) {
      if (!keep && String(line.variant_id) === monthly) keep = line;
    });

    if (!keep) {
      return finish(
        removeAllGiftLines(cart).then(function () {
          return addGiftLine(monthly);
        }),
        true
      );
    }

    var work = state.giftLines
      .filter(function (line) {
        return line !== keep;
      })
      .map(function (line) {
        return line.key;
      });
    if (keep.quantity !== 1) work.push(keep.key);
    if (!work.length) return Promise.resolve(cart);

    return finish(
      work.reduce(function (chain, key) {
        return chain.then(function () {
          return changeLine(key, key === keep.key ? 1 : 0);
        });
      }, Promise.resolve())
    );
  }

  function announceCartChange(cart) {
    ROOT.dispatchEvent(new CustomEvent('orvea:cart:updated', { detail: { cart: cart }, bubbles: true }));
  }

  function paintReward(cart) {
    $$('[data-orvea-cart-count-plain]').forEach(function (el) {
      el.textContent = cart.item_count;
    });

    var root = $('[data-orvea-reward]');
    if (!root) return;

    var tShip = parseInt(root.getAttribute('data-threshold-ship'), 10) || 0;
    var tGift = parseInt(root.getAttribute('data-threshold-gift'), 10) || 0;
    var state = readRewardState(cart);
    var eligible = state.eligible;

    var shipDone = eligible >= tShip;
    var giftDone = eligible >= tGift;

    /* Same two-line rule as the Liquid: the headline states the last reward
       won, the subline states the next one. Between the two thresholds the
       free-shipping confirmation therefore stays on screen. */
    var msgShip = shipDone
      ? root.getAttribute('data-tpl-ship-done') || ''
      : String(root.getAttribute('data-tpl-ship-to') || '').replace(
          '[importe]',
          formatMoneyShort(Math.max(tShip - eligible, 0))
        );
    var msgGift = giftDone
      ? root.getAttribute('data-tpl-gift-done') || ''
      : String(root.getAttribute('data-tpl-gift-to') || '').replace(
          '[importe]',
          formatMoneyShort(Math.max(tGift - eligible, 0))
        );

    /* Only a member who will actually receive the gift of the month sees
       "gift unlocked". Without a session, above the threshold, the headline
       stays on free shipping and the gift zone shows the members-only
       message instead. */
    var isMember = root.getAttribute('data-member') === 'true';
    var memberGift = isMember && String(root.getAttribute('data-monthly-variant') || '0') !== '0';

    var headline = msgShip;
    var subline = '';
    if (shipDone) subline = msgGift;
    if (giftDone) {
      if (memberGift) {
        headline = msgGift;
        subline = msgShip;
      } else {
        headline = root.getAttribute('data-tpl-ship-done') || '';
        subline = '';
      }
    }

    var headlineEl = $('[data-orvea-reward-headline]', root);
    if (headlineEl) headlineEl.textContent = headline;

    var sublineEl = $('[data-orvea-reward-subline]', root);
    if (sublineEl) {
      sublineEl.textContent = subline;
      sublineEl.hidden = !subline;
    }

    var fill = $('[data-orvea-reward-fill]', root);
    if (fill && tGift > 0) {
      fill.style.width = Math.min(Math.round((eligible / tGift) * 100), 100) + '%';
    }

    $$('[data-reached]', root).forEach(function (node) {
      var isGift = node.className.indexOf('--gift') !== -1;
      node.setAttribute('data-reached', (isGift ? giftDone : shipDone) ? 'true' : 'false');
    });

    var giftZone = $('[data-orvea-reward-gift]', root);
    if (giftZone) giftZone.hidden = !(giftDone && (!isMember || memberGift));

    root.setAttribute('data-eligible', eligible);
    root.setAttribute('data-gift-variant', state.giftLine ? String(state.giftLine.variant_id) : '0');
  }

  /* The reward block has no controls of its own any more — nothing to choose,
     nothing to change — so binding only marks the root as seen. Kept as a
     function because `init()` and the member-state refresh call it. */
  function initReward(scope) {
    var root = $('[data-orvea-reward]', scope) || $('[data-orvea-reward]');
    if (!root || root.dataset.orveaRewardBound === 'true') return;
    root.dataset.orveaRewardBound = 'true';
  }

  /* Every cart change re-validates eligibility against the real cart. */
  on(ROOT, 'orvea:cart:updated', function (event) {
    var cart = event.detail && event.detail.cart;
    if (!cart) return;
    paintReward(cart);
    paintGiftCard(cart);

    /* The gift is an ordinary cart line, so the drawer's item list, its item
       count and its totals all have to move with it — otherwise the list and
       the cart disagree, and the customer only finds out at checkout. Horizon
       re-renders the drawer for its own mutations; this one is ours.
       `refreshCartDrawer` is declared further down; declarations hoist. */
    refreshCartDrawer().then(function (refreshed) {
      if (refreshed) paintReward(cart);
    });
  });

  /**
   * Membership is Shopify's session, and the drawer is server-rendered, so the
   * way to notice a login that happened in another tab is to ask the server
   * for the section again. Cheap, and only when the tab regains attention.
   */
  var memberCheckAt = 0;
  function refreshMemberState() {
    var root = $('[data-orvea-reward]');
    if (!root) return;
    if (Date.now() - memberCheckAt < 4000) return;
    memberCheckAt = Date.now();

    fetch(cartRoot() + '?sections=cart-drawer-section', { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        var html = data['cart-drawer-section'];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-orvea-reward]');
        var current = $('[data-orvea-reward]');
        if (!fresh || !current) return;
        if (fresh.getAttribute('data-member') === current.getAttribute('data-member')) return;

        current.replaceWith(fresh);
        initReward(ROOT);
        /* Logging in or out changes who gets the gift: re-run the guard so
           the gift line is added (member) or removed (guest) straight away. */
        fetchCart().then(function (cart) {
          paintReward(cart);
          return enforceGiftEligibility(cart);
        });
      })
      .catch(function () {
        /* offline or section unavailable — the next page load will correct it */
      });
  }

  on(ROOT, 'visibilitychange', function () {
    if (!ROOT.hidden) refreshMemberState();
  });
  on(window, 'focus', refreshMemberState);
  on(window, 'pageshow', function (event) {
    if (event.persisted) refreshMemberState();
  });

  /* ----------------------------------------------------------------------
     Cart drawer — opening and refreshing it after an ORVEA "add".

     Horizon opens the drawer by itself, but only for mutations that go through
     its own components: <cart-drawer-component> listens for the Shopify
     Standard Event `shopify:cart:lines-update` and opens when the action is an
     add and the merchant left "open the cart drawer" on. The ORVEA product
     card has its own "+" button and posts straight to /cart/add.js, so Horizon
     never hears about it. Instead of re-implementing the drawer, we speak its
     language: we dispatch the very same standard event, with the add's promise
     attached, so Horizon opens and settles the drawer exactly as it does for
     its own forms — including respecting the setting when it is off.

     Refreshing the contents is the other half. <theme-drawer id="cart-drawer">
     carries `data-skip-node-update`, so Horizon's morphing deliberately leaves
     the drawer alone; its contents are re-rendered by the cart components that
     own the mutation. Ours doesn't, so we ask the Section Rendering API for the
     drawer section and swap in only `[data-hydration-key="cart-drawer-inner"]`.
     The <dialog> element itself is untouched, so an open drawer stays open and
     nothing flickers. The HTML is Shopify's own render of the section, which is
     why the line items, the totals, the reward block and the item count all end
     up consistent with the real cart rather than patched one by one.
     -------------------------------------------------------------------- */

  var ORVEA_ADD_SOURCE = 'orvea-quick-add';
  var ADDED_NOTICE_MS = 3600;
  var addedTimer = null;
  var standardEventsPromise = null;

  function cartDrawerInner() {
    return $('#cart-drawer [data-hydration-key="cart-drawer-inner"]');
  }

  /* Horizon's cart page only reacts to a standard cart event once
     <cart-items-component> has been upgraded. On a first load the gift can be
     settled before that happens, and an event dispatched then is simply lost:
     the line list and the totals would keep the state the server rendered.
     So wait for the upgrade instead of shouting into an empty room. Resolves
     straight away on any page that has no such element. */
  function cartPageReady() {
    if (!$('cart-items-component')) return Promise.resolve();
    if (typeof customElements === 'undefined' || !customElements.whenDefined) return Promise.resolve();
    return customElements.whenDefined('cart-items-component').catch(function () {});
  }

  function refreshCartDrawer() {
    if (!cartDrawerInner()) return Promise.resolve(false);

    return fetch(cartRoot() + '?sections=cart-drawer-section', { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        var html = data['cart-drawer-section'];
        var current = cartDrawerInner();
        if (!html || !current) return false;

        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-hydration-key="cart-drawer-inner"]');
        if (!fresh) return false;

        /* importNode, not the parsed node: it is what upgrades the custom
           elements inside (quantity selectors, cart items) on insertion. */
        var adopted = ROOT.importNode(fresh, true);
        current.replaceWith(adopted);
        init(adopted);
        return true;
      })
      .catch(function () {
        return false;
      });
  }

  /**
   * The confirmation has to survive the drawer being re-rendered underneath it:
   * both Horizon and `refreshCartDrawer` replace the subtree the notice lives
   * in, and whichever of them lands last would otherwise wipe it. So the state
   * lives here as a deadline, and a MutationObserver re-applies it to whatever
   * notice element is currently in the DOM until the deadline passes.
   */
  var addedUntil = 0;
  var addedObserver = null;

  function paintAddedNotice() {
    var el = $('[data-orvea-cart-added]');
    if (!el) return;
    if (Date.now() >= addedUntil) {
      el.removeAttribute('data-visible');
      el.hidden = true;
      return;
    }
    if (el.hidden === false && el.getAttribute('data-visible') === 'true') return;
    el.hidden = false;
    /* Force a reflow so the browser registers the hidden "from" state before
       the visible one, which is what gives the transition something to animate.
       A reflow rather than requestAnimationFrame on purpose: rAF never fires in
       a background tab, and the notice would then stay invisible in exactly the
       case where someone adds from a tab they are not looking at. */
    void el.offsetWidth;
    el.setAttribute('data-visible', 'true');
  }

  function showAddedNotice() {
    var drawer = $('#cart-drawer');
    if (!drawer) return;

    addedUntil = Date.now() + ADDED_NOTICE_MS;
    paintAddedNotice();

    if (!addedObserver && typeof MutationObserver === 'function') {
      addedObserver = new MutationObserver(function () {
        paintAddedNotice();
      });
      addedObserver.observe(drawer, { childList: true, subtree: true });
    }

    if (addedTimer) window.clearTimeout(addedTimer);
    addedTimer = window.setTimeout(function () {
      addedTimer = null;
      paintAddedNotice();
    }, ADDED_NOTICE_MS + 40);
  }

  /** Shopify's own event class when the theme exposes it; a duck otherwise. */
  function standardEvents() {
    if (!standardEventsPromise) {
      standardEventsPromise = Promise.resolve()
        .then(function () {
          return import('@shopify/events');
        })
        .catch(function () {
          return null;
        });
    }
    return standardEventsPromise;
  }

  /**
   * @param {Promise} promise the mutation Horizon should settle against.
   * @param {string} [action] 'add' by default. Pass 'update' for a removal,
   *        so the cart re-renders without anything claiming a line was added.
   */
  function announceAdd(promise, action) {
    return standardEvents().then(function (mod) {
      var payload = {
        action: action || 'add',
        source: ORVEA_ADD_SOURCE,
        sourceElement: ROOT.body,
        promise: promise
      };
      var event;
      if (mod && typeof mod.CartLinesUpdateEvent === 'function') {
        event = new mod.CartLinesUpdateEvent(payload);
      } else {
        event = new CustomEvent('shopify:cart:lines-update', { bubbles: true, cancelable: true });
        event.action = payload.action;
        event.source = payload.source;
        event.sourceElement = payload.sourceElement;
        event.promise = payload.promise;
      }
      ROOT.dispatchEvent(event);
    });
  }

  /**
   * The one add path for ORVEA's own buttons: post, tell Horizon, refresh the
   * drawer from the server, confirm. Returns the fresh cart.
   */
  function orveaAdd(items) {
    var settled = addToCart({ items: items }).then(function (data) {
      return fetchCart().then(function (cart) {
        return { cart: cart, detail: data };
      });
    });

    /* Dispatched immediately, not after the round trip: Horizon opens the
       drawer straight away and settles it when the promise resolves. */
    announceAdd(settled);

    return settled.then(function (result) {
      return refreshCartDrawer().then(function () {
        showAddedNotice();
        return result.cart;
      });
    });
  }

  /* Adds that came from Horizon's own forms (the product page) already
     re-render the drawer; all they are missing is the confirmation. */
  on(ROOT, 'shopify:cart:lines-update', function (event) {
    if (event.action !== 'add' || event.source === ORVEA_ADD_SOURCE) return;
    var promise = event.promise && typeof event.promise.then === 'function' ? event.promise : Promise.resolve();
    promise.then(
      function () {
        window.setTimeout(showAddedNotice, 400);
      },
      function () {
        /* the add failed — nothing to confirm */
      }
    );
  });

  /* ----------------------------------------------------------------------
     Navigation — desktop mega-menu and the drawer's accordion.

     Both read the same Shopify menu; only the presentation differs. The
     desktop panels open on hover and on keyboard focus, and close on Escape,
     on click outside, and when focus leaves the item. Pointer intent is
     debounced so brushing past a trigger on the way to another one does not
     flash a panel open.
     -------------------------------------------------------------------- */

  var MEGA_OPEN_DELAY = 90;
  var MEGA_CLOSE_DELAY = 180;
  var megaDocBound = false;
  var megaClearTimers = function () {};

  function megaItems(scope) {
    return $$('[data-orvea-mega-item]', scope);
  }

  function isDesktopNav() {
    return window.matchMedia && window.matchMedia('(min-width: 990px)').matches;
  }

  /**
   * Point the image area at one category's group. Groups are stacked in the
   * same grid cell, so this is purely a cross-fade: nothing reflows.
   */
  function showMegaGroup(panel, groupId) {
    var groups = $$('[data-orvea-mega-group]', panel);
    if (!groups.length) return;

    var target = groupId ? panel.querySelector('#' + CSS.escape(groupId)) : groups[0];
    if (!target) target = groups[0];

    groups.forEach(function (group) {
      var active = group === target;
      group.setAttribute('data-active', active ? 'true' : 'false');
      if (active) group.removeAttribute('aria-hidden');
      else group.setAttribute('aria-hidden', 'true');
    });

    $$('[data-orvea-mega-cat]', panel).forEach(function (cat) {
      cat.setAttribute('data-current', cat.getAttribute('aria-controls') === target.id ? 'true' : 'false');
    });
  }

  /** Back to the first category, so a panel never reopens mid-story. */
  function resetMegaGroup(panel) {
    showMegaGroup(panel, null);
  }

  function initMegaCategories(panel) {
    if (panel.dataset.orveaCatsBound === 'true') return;
    panel.dataset.orveaCatsBound = 'true';

    $$('[data-orvea-mega-cat]', panel).forEach(function (cat) {
      var groupId = cat.getAttribute('aria-controls');
      var show = function () {
        showMegaGroup(panel, groupId);
      };
      on(cat, 'mouseenter', show);
      on(cat, 'focus', show);
    });

    resetMegaGroup(panel);
  }

  function setMegaState(item, open) {
    var panel = $('[data-orvea-mega-panel]', item);
    if (!panel) return;

    $$('[aria-controls]', item).forEach(function (control) {
      if (control.hasAttribute('aria-expanded')) {
        control.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
    });

    if (open) {
      initMegaCategories(panel);
      panel.hidden = false;
      /* Reflow so the opening transition actually runs from its start state. */
      void panel.offsetWidth;
      panel.classList.add('is-open');
    } else {
      panel.classList.remove('is-open');
      panel.hidden = true;
      resetMegaGroup(panel);
    }

    item.setAttribute('data-open', open ? 'true' : 'false');
  }

  function closeAllMega(scope, except) {
    megaItems(scope).forEach(function (item) {
      if (item !== except) setMegaState(item, false);
    });
  }

  function initMegaMenu(scope) {
    var root = $('[data-orvea-mega-root]', scope) || $('[data-orvea-mega-root]');
    if (!root) return;
    if (root.dataset.orveaBound === 'true') return;
    root.dataset.orveaBound = 'true';

    var openTimer = null;
    var closeTimer = null;

    function clearTimers() {
      if (openTimer) window.clearTimeout(openTimer);
      if (closeTimer) window.clearTimeout(closeTimer);
      openTimer = null;
      closeTimer = null;
    }

    function scheduleOpen(item) {
      clearTimers();
      openTimer = window.setTimeout(function () {
        closeAllMega(root, item);
        setMegaState(item, true);
      }, MEGA_OPEN_DELAY);
    }

    function scheduleClose() {
      clearTimers();
      closeTimer = window.setTimeout(function () {
        closeAllMega(root);
      }, MEGA_CLOSE_DELAY);
    }

    megaItems(root).forEach(function (item) {
      on(item, 'mouseenter', function () {
        if (!isDesktopNav()) return;
        scheduleOpen(item);
      });

      on(item, 'mouseleave', function () {
        if (!isDesktopNav()) return;
        scheduleClose();
      });

      /* Keyboard and touch: the chevron toggles, so the sibling link stays a
         plain link to the category page. */
      $$('[data-orvea-mega-trigger]', item).forEach(function (trigger) {
        on(trigger, 'click', function (event) {
          event.preventDefault();
          var isOpen = item.getAttribute('data-open') === 'true';
          clearTimers();
          closeAllMega(root, item);
          setMegaState(item, !isOpen);
          if (!isOpen) {
            var first = $('a.orvea-mega__cat', item);
            if (first && isDesktopNav()) first.focus();
          }
        });
      });

      on(item, 'focusin', function () {
        if (!isDesktopNav()) return;
        clearTimers();
        closeAllMega(root, item);
        setMegaState(item, true);
      });

      on(item, 'focusout', function (event) {
        if (!isDesktopNav()) return;
        if (item.contains(event.relatedTarget)) return;
        scheduleClose();
      });
    });

    megaClearTimers = clearTimers;
    bindMegaDocumentListeners();
  }

  /**
   * Escape, click-outside and breakpoint changes are document-wide, so they are
   * bound exactly once for the page rather than per header instance. The Theme
   * Editor re-renders the header on every edit, which would otherwise stack a
   * fresh set of document listeners (each holding a detached root) on each save.
   * They look the root up at event time, so they always act on the live header.
   */
  function bindMegaDocumentListeners() {
    if (megaDocBound) return;
    megaDocBound = true;

    function currentRoot() {
      return $('[data-orvea-mega-root]');
    }

    on(ROOT, 'keydown', function (event) {
      if (event.key !== 'Escape') return;
      var root = currentRoot();
      if (!root) return;
      var open = root.querySelector('[data-orvea-mega-item][data-open="true"]');
      if (!open) return;
      megaClearTimers();
      setMegaState(open, false);
      var trigger = $('[data-orvea-mega-trigger], .orvea-header__rail-link', open);
      if (trigger) trigger.focus();
    });

    on(ROOT, 'click', function (event) {
      var root = currentRoot();
      if (!root || root.contains(event.target)) return;
      megaClearTimers();
      closeAllMega(root);
    });

    /* Crossing the breakpoint leaves a panel stranded; reset it. */
    if (window.matchMedia) {
      var mq = window.matchMedia('(min-width: 990px)');
      var reset = function () {
        var root = currentRoot();
        megaClearTimers();
        if (root) closeAllMega(root);
      };
      if (mq.addEventListener) mq.addEventListener('change', reset);
      else if (mq.addListener) mq.addListener(reset);
    }
  }

  /** The drawer's accordion. Delegated, so re-rendered menus keep working. */
  function initNavAccordion(scope) {
    $$('[data-orvea-menu]', scope).forEach(function (menu) {
      if (menu.dataset.orveaAccBound === 'true') return;
      menu.dataset.orveaAccBound = 'true';

      on(menu, 'click', function (event) {
        var toggle = event.target.closest ? event.target.closest('[data-orvea-acc-toggle]') : null;
        if (!toggle || !menu.contains(toggle)) return;

        event.preventDefault();
        var panel = ROOT.getElementById(toggle.getAttribute('aria-controls'));
        if (!panel) return;

        var isOpen = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        panel.hidden = isOpen;
      });
    });
  }

  /* ----------------------------------------------------------------------
     Boot + Theme Editor re-init
     -------------------------------------------------------------------- */
  function init(scope) {
    initAutoSubmit(scope);
    initRotators(scope);
    initQuickAdd(scope);
    initThresholds(scope);
    initAccordions(scope);
    initRails(scope);
    initMenu(scope);
    initReward(scope);
    initMegaMenu(scope);
    initNavAccordion(scope);
    initQuantity(scope);
    initVariantPickers(scope);
  }

  if (ROOT.readyState === 'loading') {
    on(ROOT, 'DOMContentLoaded', function () {
      init(ROOT);
    });
  } else {
    init(ROOT);
  }

  ROOT.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  ROOT.addEventListener('shopify:block:select', function (event) {
    var panel = event.target.querySelector ? event.target.querySelector('[data-orvea-acc-panel]') : null;
    if (panel) {
      panel.setAttribute('data-open', 'true');
      var trigger = ROOT.querySelector('[aria-controls="' + panel.id + '"]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }
  });

  window.ORVEA = window.ORVEA || {};
  window.ORVEA.refreshCartCount = refreshCartCount;
  window.ORVEA.formatMoney = formatMoney;
  window.ORVEA.syncThresholds = syncThresholds;

  /**
   * Read-only view of the threshold state, for code that is added later
   * (e.g. another app block) and should not duplicate any of this logic.
   */
  window.ORVEA.thresholds = {
    /** @returns {number|null} current cart total in cents, null before first read */
    getTotal: function () {
      return lastKnownTotal;
    },
    /** @param {number} index 1 = free shipping, 2 = gift */
    isUnlocked: function (index) {
      var root = $('[data-orvea-thresholds]');
      if (!root) return false;
      return root.getAttribute('data-unlocked-' + index) === 'true';
    },
    /** @returns {number|null} threshold value in cents */
    getThreshold: function (index) {
      var root = $('[data-orvea-thresholds]');
      if (!root) return null;
      return parseInt(root.getAttribute('data-threshold-' + index), 10) || null;
    },
    /** Force a re-read of /cart.js and repaint. */
    sync: syncThresholds
  };
})();
